import i18next, { type TFunction } from "i18next";
import { formatUsageCost } from "./money";
import type { AiModel, InferenceDay, InferenceTotals, ModelCategory } from "./types";

/**
 * The AI inference endpoint, and the snippets that show how to reach it.
 *
 * Baked at build time like `VITE_API_URL`, because it is a property of the
 * deployment rather than of the tenant. The default matches the port
 * maxlayer-inference's `wrangler dev` binds, so a local checkout works with no
 * `.env.local` at all.
 */
export const INFERENCE_URL =
  (import.meta.env.VITE_INFERENCE_URL as string | undefined) ?? "http://localhost:8788";

/** The base URL an OpenAI-compatible SDK wants: the `/v1`, without the path. */
export function inferenceBaseUrl(): string {
  return `${INFERENCE_URL.replace(/\/+$/, "")}/v1`;
}

/**
 * The placeholder shown where a key would go.
 *
 * Never a real key. Keys are displayed once, at creation, and pasting one into
 * a snippet on a page that anybody with `viewer` can open would put it on a
 * screen long after the one moment it was meant to be visible.
 */
export const KEY_PLACEHOLDER = "$MAXLAYER_API_KEY";

export function partitionAiModels(models: AiModel[]) {
  const ordered = [...models].sort((a, b) => b.created - a.created || a.id.localeCompare(b.id));
  const featured = ordered
    .filter(
      (model): model is AiModel & { featured_rank: number } =>
        typeof model.featured_rank === "number" &&
        Number.isInteger(model.featured_rank) &&
        model.featured_rank > 0,
    )
    .sort((a, b) => a.featured_rank - b.featured_rank || a.id.localeCompare(b.id));
  const featuredIds = new Set(featured.map((model) => model.id));

  return { featured, regular: ordered.filter((model) => !featuredIds.has(model.id)) };
}

export function curlSnippet(modelId: string): string {
  return `curl ${inferenceBaseUrl()}/chat/completions \\
  -H "Authorization: Bearer ${KEY_PLACEHOLDER}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "${modelId}",
    "messages": [{"role": "user", "content": "Hello"}]
  }'`;
}

export function nodeSnippet(modelId: string): string {
  return `import OpenAI from "openai";

const client = new OpenAI({
  apiKey: process.env.MAXLAYER_API_KEY,
  baseURL: "${inferenceBaseUrl()}",
});

const response = await client.chat.completions.create({
  model: "${modelId}",
  messages: [{ role: "user", content: "Hello" }],
});

console.log(response.choices[0].message.content);`;
}

export function pythonSnippet(modelId: string): string {
  return `from openai import OpenAI

client = OpenAI(
    api_key=os.environ["MAXLAYER_API_KEY"],
    base_url="${inferenceBaseUrl()}",
)

response = client.chat.completions.create(
    model="${modelId}",
    messages=[{"role": "user", "content": "Hello"}],
)

print(response.choices[0].message.content)`;
}

export type SnippetLanguage = "curl" | "node" | "python";

export const SNIPPETS: Record<SnippetLanguage, { label: string; build: (model: string) => string }> =
  {
    curl: { label: "curl", build: curlSnippet },
    node: { label: "Node", build: nodeSnippet },
    python: { label: "Python", build: pythonSnippet },
  };

/**
 * Token counts, at the magnitude people actually read them.
 *
 * A month of agent traffic is tens of millions, and printing that in full makes
 * a column nobody can compare down. Below ten thousand the exact figure is
 * still useful — it is what somebody testing an integration is checking.
 */
export function formatTokens(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 10_000) return `${Math.round(count / 1_000)}k`;
  return count.toLocaleString();
}

/**
 * A token count on a row that also shows what the request cost.
 *
 * Zero means "not measured in tokens", and it needs saying differently from a
 * count. A video request reports no tokens at all — there are none to report,
 * the charge is what the generation cost — so the activity row rendered `0`
 * beside `$0.88` and the only available reading was that the meter had failed.
 *
 * A dash is the honest answer and is right for the other zero too: a request
 * that failed before it consumed anything did not use nought tokens, it used
 * none. `formatTokens` keeps rendering `0` for a context window, where zero is a
 * number rather than an absence.
 */
export function formatRequestTokens(count: number): string {
  return count === 0 ? "—" : formatTokens(count);
}

/**
 * A token count on a single request, in full.
 *
 * `formatRequestTokens` abbreviates — 36,649 becomes "37k" — which is right in a
 * breakdown, where the figure is a magnitude being compared against others, and
 * wrong on one request, where it is the exact number somebody is reconciling
 * against their own client's logs. Losing 649 tokens to rounding is fine for a
 * chart axis and not fine for a receipt.
 *
 * Carries its unit, because the row beside it holds money and two bare numbers
 * in adjacent columns invite reading one as the other.
 *
 * Zero still renders as a dash, for the reason `formatRequestTokens` does: a
 * video request reports no tokens at all — there are none, the charge is what
 * the generation cost — and "0 tok" beside a real cost reads as a broken meter
 * rather than as a different unit of billing.
 */
export function formatExactTokens(count: number): string {
  return count === 0 ? "—" : `${count.toLocaleString()} tok`;
}

/**
 * What the usage chart can plot.
 *
 * Four series rather than one, because they answer different questions and a
 * customer arrives holding one of them. Spend is "what is this costing me";
 * tokens is "is my prompt or my output the expensive half"; requests is "did my
 * integration actually run" — which a cost chart cannot answer, because a day
 * of failed calls costs nothing and looks identical to a day of no calls. Cached
 * is "is my client reusing its context or re-sending it", which is the shape a
 * coding tool's cost problem takes before it shows up as a cost problem.
 *
 * Out here rather than in the page so the accessors can be tested. Reading the
 * wrong field would produce a chart that is plausible, wrong, and impossible to
 * spot by eye.
 */
/* Resolved per call, as cron.ts does, so a chart label follows a language
   switch without the series table being rebuilt. */
const aiT = () => i18next.getFixedT(null, "ai");

export const USAGE_SERIES = [
  {
    key: "spend" as const,
    value: (day: InferenceDay) => day.costMicros,
    /*
     * A single day of token spend is smaller than a month of it and more often
     * sub-cent, so this is the series that needed the extra places most: at two
     * decimals a chart of light traffic labels every bar $0.00 and reads as a
     * chart of nothing.
     */
    format: (value: number, currency: string) => formatUsageCost(value, currency),
  },
  {
    key: "tokens" as const,
    value: (day: InferenceDay) => day.inputTokens + day.outputTokens,
    format: (value: number) => aiT()("series.formatTokens", { tokens: formatTokens(value) }),
  },
  {
    key: "requests" as const,
    value: (day: InferenceDay) => day.requests,
    format: (value: number) => aiT()("series.formatRequests", { count: value }),
  },
  {
    key: "cached" as const,
    /*
     * Cache reads only, and deliberately not summed with writes.
     *
     * The two move a bill in opposite directions — a read is a prompt the
     * provider had already seen, a write is the call that put it there — so one
     * combined line would be a chart of two different things. Reads are the one
     * worth watching over time: a coding tool's cache hit rate climbing is what
     * a healthy long-running session looks like, and it dropping to nothing is
     * the shape of a tool that has started re-sending its whole context.
     *
     * Charted against `inputTokens` in the same series would be better still and
     * needs a two-line chart this component does not have.
     */
    value: (day: InferenceDay) => day.cachedInputTokens,
    format: (value: number) => aiT()("series.formatCached", { tokens: formatTokens(value) }),
  },
];

export type UsageSeriesKey = (typeof USAGE_SERIES)[number]["key"];

/**
 * What the usage strip shows before the first response arrives.
 *
 * One definition rather than a literal at each of the two call sites. Both tabs
 * render the same strip, and a totals shape that grows a field is a shape two
 * inline literals will grow it at different times — TypeScript catches the
 * second one missing, which means the fix is made twice under compiler pressure
 * rather than once on purpose.
 *
 * Zero is honest for every field: nothing loaded is no traffic to describe.
 */
export const EMPTY_TOTALS: InferenceTotals = {
  costMicros: 0,
  requests: 0,
  inputTokens: 0,
  outputTokens: 0,
  cachedInputTokens: 0,
  cacheWriteTokens: 0,
  reasoningTokens: 0,
  inputCostMicros: 0,
  outputCostMicros: 0,
  cachedSavingMicros: 0,
};

export function usageSeries(key: UsageSeriesKey): (typeof USAGE_SERIES)[number] {
  return USAGE_SERIES.find((series) => series.key === key) ?? USAGE_SERIES[0]!;
}

/* ------------------------------------------------------- browsing the models */

/**
 * A rate as an integer, for sorting and comparing.
 *
 * The API sends prices as decimal strings so nothing rounds them in transit, and
 * it also sends the same figure in micros. Sorting reads the micros: sorting the
 * strings would order `$0.9` above `$10` the moment a rate reaches double digits.
 */
export function rateMicros(model: AiModel, side: "input" | "output"): number {
  /*
   * Base, because base is what the catalogue shows. With one markup across the
   * catalogue this looks like a distinction without a difference — but a model
   * carrying its own markup breaks that, and then "cheapest in" would produce a
   * column of numbers that does not ascend.
   */
  return side === "input"
    ? model.pricing.base_input_micros_per_million_tokens
    : model.pricing.base_output_micros_per_million_tokens;
}

/**
 * Whether the charge comes from what a request cost rather than from a rate.
 *
 * Image and video models are the ones: the provider publishes no per-token price
 * for them, so every figure in `pricing` is zero and the bill is the cost of the
 * request plus the same markup as everything else. Speech was a third until the
 * shelf was removed — nothing sold on it, and it is gone from the database's
 * category constraint as well as from here.
 *
 * Three separate things read this, and each of them renders a falsehood without
 * it — a rate column showing `$0.00`, a "free" chip offering models that bill
 * real money, and a cheapest-first order led by the 65 models with no rate at
 * all. Absent means `token`, which is what every model was before image and
 * video generation.
 */
export function isCostPriced(model: AiModel): boolean {
  return model.billing_mode === "upstream_cost";
}

/**
 * Which of the four ways a listed model can be priced this one is.
 *
 * The catalogue carries two billing bases and four readings, and each surface
 * had been rediscovering them from a different pair of fields — `billing_mode`
 * in the tag, a zero rate in the formatter, `price_lines.length` in the row.
 * Three conditions describing one fact is how a row came to carry a tag and a
 * price block that said different things about the same model.
 *
 *   token     a rate card. Per million tokens in and out.
 *   free      a rate card the provider publishes at zero. Both sides, since an
 *             embedding's zero output rate is arithmetic rather than a price.
 *   unit      no rate card, and the provider's per-unit prices are known — per
 *             image, per second, per megapixel, often several at once.
 *   unstated  no rate card and no published price either. Three image models.
 *
 * `unit` and `unstated` bill identically, at what the request cost plus the
 * markup. They are separate readings because one has a figure to show before the
 * call and the other has only a dash, and that dash has to mean "the supplier
 * publishes none" rather than "we did not look".
 *
 * The same four, by the same names, exist in `maxlayer-web`'s
 * `model-marketplace.mjs`. Two copies rather than a shared package: the
 * repositories deploy separately and share no build, which is the standing trade
 * in this codebase.
 */
export type PricingBasis = "token" | "free" | "unit" | "unstated";

export function pricingBasis(model: AiModel): PricingBasis {
  if (isCostPriced(model)) {
    return (model.price_lines ?? []).length > 0 ? "unit" : "unstated";
  }
  return isFreeModel(model) ? "free" : "token";
}

/** How each basis reads on a row that has room for a word rather than a figure. */
export const PRICING_BASIS_LABELS: Record<PricingBasis, string> = {
  token: "per token",
  free: "free",
  unit: "priced per request",
  unstated: "priced per request",
};

/**
 * A translator for this module's word tables.
 *
 * The catalogue's units, price rows and sort names are all read by components
 * that already hold `useTranslation("ai")`, so that is the signature: passing
 * `t` costs a caller nothing and keeps the keys checked against the English
 * file. The casts below are where a key is genuinely dynamic — a billing unit
 * arrives as a string from the supplier — and there is nothing to check against.
 */
export type CatalogueT = TFunction<"ai">;

/* ------------------------------------------------------- one price, four ways */

/**
 * What a model charges, in the one shape every basis actually shares.
 *
 * Four pricing readings reached this page and each had been given its own
 * treatment: a pair of columns for a rate card, a spanned cell for per-unit
 * prices, a dash plus a tag for an unpriced one, and the word "Free". Four
 * treatments meant four things to learn, a tag explaining a column, and a
 * paragraph under the table explaining the tag.
 *
 * They are not four shapes. Every one of them is a list of label→figure rows,
 * a caption naming the unit those figures are in, and — where the charge is not
 * the figures shown — a note saying so:
 *
 *   token     in  $0.029      free   Free      unit    text   $0.20
 *             out $0.253                               image  $0.45
 *             per 1M tokens                            audio  $6.50
 *                                                      per 1M tokens
 *                                                      billed per request
 *
 * Written once here so the renderer has no branches left and cannot invent a
 * fifth reading. `maxlayer-web` carries the same function over the same
 * catalogue — two copies rather than a shared package, which is the standing
 * trade in this codebase.
 */
export interface PricePanel {
  rows: { label: string; value: string }[];
  /** The unit the figures are in. Null when each row names its own. */
  caption: string | null;
  /** Set when the charge is not the figures above. */
  note: string | null;
  /** Lines beyond the cap, counted rather than dropped. */
  andMore: number;
}

/**
 * How one price line reads as a row label.
 *
 * Two rules, decided by whether the lines share a unit, because the thing that
 * distinguishes them differs:
 *
 *   * **Same unit** — an embedding model's three per-token rates — so the unit is
 *     redundant on every row and the *kind* is what tells them apart: text,
 *     image, audio. The unit moves to the caption, where it is said once.
 *   * **Different units** — an image model charging per image and per megapixel —
 *     so the unit is exactly what distinguishes them and becomes the label.
 *
 * A resolution in the billable wins over both, because two video lines that are
 * both "per second" are told apart by nothing else.
 */
export function priceRowLabel(
  line: { billable: string; unit: string },
  sharedUnit: boolean,
  t: CatalogueT,
): string {
  const resolution = /(\d{3,4}p)/.exec(line.billable)?.[1];
  if (resolution) return resolution;

  if (sharedUnit) {
    const kind = line.billable.replace(/^(input|output)_/, "").replace(/_/g, " ");
    if (kind && kind !== line.unit) return kind;
  }

  return unitLabel(line.unit, t);
}

/**
 * What a billing unit is called, or the raw unit where we have no word for it.
 *
 * A supplier can publish a unit this catalogue has never seen, and the raw
 * string is a better answer than a blank: "per widget" is legible, "per" is not.
 */
function unitLabel(unit: string, t: CatalogueT): string {
  const key = `price.unit.${unit}`;
  const word = t(key as "price.unit.token");
  return word === key ? unit : word;
}

/** A figure on its own, without the unit — the unit is the label or the caption. */
export function formatPriceValue(usd: number, currency = "USD"): string {
  return formatModelRate(String(usd), currency);
}

export function pricePanel(model: AiModel, t: CatalogueT, limit = 3): PricePanel {
  const currency = model.pricing.currency;
  const basis = pricingBasis(model);

  /*
   * "Free" is a statement rather than a rate, so it is one row with no label and
   * no caption. Labelling it "in" and repeating it for "out" would render the
   * same word twice about a model that charges nothing either way.
   */
  if (basis === "free") {
    return { rows: [{ label: "", value: t("price.free") }], caption: null, note: null, andMore: 0 };
  }

  if (basis === "token") {
    const rows = [{ label: t("price.in"), value: formatModelBasePrice(model, "input") }];

    /*
     * Output only when there is output to charge for.
     *
     * An embedding model emits no tokens, so its output rate is zero as
     * arithmetic rather than as a price — and a column reading `$0.00` on the
     * whole embedding shelf was a figure nobody could act on sitting where a
     * price belongs. Dropped rather than dashed: the row is complete without it.
     */
    if (model.pricing.base_output_micros_per_million_tokens > 0) {
      rows.push({ label: t("price.out"), value: formatModelBasePrice(model, "output") });
    }

    /*
     * The two cache rates are deliberately **not** rows here.
     *
     * They were, briefly. A catalogue row is read to answer "what does this
     * model cost me", and the honest answer to that is the input and output
     * pair: those bill every request, where the cache rates bill only a caller
     * who has opted into prompt caching — a minority of the catalogue publishes
     * them at all, and a four-row price block on every model made the two
     * figures that always apply harder to compare across models.
     *
     * They remain published on `/v1/models`, both as the sell price and as the
     * provider's base, so a client that prices its own requests still has them.
     * And the AI usage view shows cached tokens and what the discount was worth,
     * which is where a customer actually meets caching: after a request, against
     * their own traffic, rather than as a rate on a shelf.
     *
     * The one worth remembering if this is revisited: the cache-*write* rate is
     * the only figure here that can push a bill **above** the headline input
     * rate. It is the rate whose absence a customer could notice.
     */

    return {
      rows,
      caption: t("price.per", { unit: unitLabel("M tokens", t) }),
      note: null,
      andMore: 0,
    };
  }

  /*
   * Cost-priced, with nothing published. Three image models are genuinely here.
   * A dash rather than an empty panel, because the row still has to say
   * something, and the note is what makes the dash mean "none published" rather
   * than "failed to load".
   */
  const { lines, andMore } = displayPriceLines(model, limit);
  if (lines.length === 0) {
    return {
      rows: [{ label: "", value: t("price.none") }],
      caption: null,
      note: t("price.perRequest"),
      andMore: 0,
    };
  }

  const sharedUnit = new Set(lines.map((line) => line.unit)).size === 1;

  return {
    rows: lines.map((line) => ({
      label: priceRowLabel(line, sharedUnit, t),
      value: formatPriceValue(line.usd, currency),
    })),
    /* Said once when every figure is in it, and left to the labels when not. */
    caption: sharedUnit ? t("price.per", { unit: unitLabel(lines[0]!.unit, t) }) : null,
    note: t("price.perRequest"),
    andMore,
  };
}

/**
 * What a generation model charges per unit, ready to render.
 *
 * Output before input, then dearest first: what a model charges to *produce*
 * something is the figure somebody compares, and the input surcharges are
 * detail. Capped so a model charging on five axes does not turn one table row
 * into a paragraph — the remainder is counted rather than dropped.
 */
export function displayPriceLines(
  model: AiModel,
  limit = 2,
): { lines: { billable: string; unit: string; usd: number }[]; andMore: number } {
  const lines = model.price_lines ?? [];
  if (lines.length === 0) return { lines: [], andMore: 0 };

  const ranked = [...lines].sort((a, b) => rank(a) - rank(b) || b.usd - a.usd);

  return { lines: ranked.slice(0, limit), andMore: Math.max(0, ranked.length - limit) };
}

/**
 * Which line the reader needs to see first, in three tiers.
 *
 * Dearest-first alone was right while only generation models had price lines: a
 * model charging per image and per input token is compared on the image, and the
 * input surcharge is detail.
 *
 * Multimodal embedding models broke that. All three of their lines are input
 * rates, and the dearest is audio at $6.50/M against a text rate of $0.20/M — so
 * pure dearest-first put the exotic rate at the top and pushed the one that
 * applies to an ordinary request under "+1 more". The cap is two lines here, so
 * "hidden" meant genuinely invisible.
 *
 * The rule that covers both: the line that applies to the ordinary request leads.
 * For a generation model that is what it produces; for an embedding model it is
 * the text rate. Everything else is a surcharge, ordered dearest-first as before.
 */
function rank(line: { billable: string }): number {
  if (line.billable.startsWith("output") || line.billable.includes("duration")) return 0;
  if (line.billable === "input_text") return 1;
  return 2;
}

/** One price line, formatted like every other rate in the table. */
export function formatPriceLine(
  line: { unit: string; usd: number },
  t: CatalogueT,
  currency = "USD",
): string {
  return `${formatModelRate(String(line.usd), currency)} / ${unitLabel(line.unit, t)}`;
}

/**
 * Whether a model costs nothing to call.
 *
 * Read from the micros rather than the decimal strings, because `"0"`,
 * `"0.000000"` and `"0.0"` are all the same rate and only one of them compares
 * equal to itself as text.
 *
 * Both sides, not either. An embedding model is priced on input alone and its
 * zero output rate is arithmetic — it emits no tokens to charge for — so
 * treating one zero as free would label most of the embedding shelf wrongly.
 */
export function isFreeModel(model: AiModel): boolean {
  /*
   * A cost-priced model is never free, whatever its rate card reads.
   *
   * Image and video models carry zeros in every pricing field because the
   * provider publishes no per-token rate for them — the charge comes from what
   * the request cost. Read as a rate, that is "free", which is a lie about a
   * model that bills real money.
   */
  if (isCostPriced(model)) return false;

  return (
    model.pricing.input_micros_per_million_tokens === 0 &&
    model.pricing.output_micros_per_million_tokens === 0
  );
}

/**
 * Prices come from the API as decimal strings so no float rounds them on the way
 * here. Trailing zeros past the fourth place are trimmed — `$0.035000` is six
 * places of precision the reader did not ask for, while `$0.000027` needs every
 * one of them.
 *
 * A model the provider publishes at zero reads "Free" rather than "$0.00". They
 * are the same number and not the same statement: `$0.00` in a column of prices
 * is what a rate that failed to load looks like, and the reader has no way to
 * tell that from a rate that is genuinely nothing. Only a wholly free model gets
 * the word — see `isFreeModel` for why one zero is not enough.
 */
/**
 * The base rate, which is what the catalogue shows.
 *
 * The provider's own rate, not the sell price beside it in the same payload. The
 * catalogue is the one surface showing it and states the difference nowhere:
 * every figure on the usage and billing pages is what was actually charged, and
 * `sell` is what every meter reads. Rendering the wrong one of these two is a
 * one-word change that nothing else would catch.
 */
export function formatModelBasePrice(model: AiModel, side: "input" | "output"): string {
  /*
   * No rate, so no figure. A dash rather than a number, because there is no
   * per-million-token price to show — not a small one, and not a zero. The row
   * carries a tag saying how it is charged instead, which is the only place that
   * fits a sentence.
   */
  if (isCostPriced(model)) return "—";
  if (isFreeModel(model)) return "Free";

  return formatModelRate(
    side === "input"
      ? model.pricing.base_input_per_million_tokens
      : model.pricing.base_output_per_million_tokens,
    model.pricing.currency,
  );
}

export function formatModelPrice(model: AiModel, side: "input" | "output"): string {
  if (isCostPriced(model)) return "—";
  if (isFreeModel(model)) return "Free";

  return formatModelRate(
    side === "input"
      ? model.pricing.input_per_million_tokens
      : model.pricing.output_per_million_tokens,
    model.pricing.currency,
  );
}

/**
 * One decimal-string rate, formatted.
 *
 * Shared by the base and sell formatters above so the two cannot render the same
 * magnitude differently — a base of $0.4220 beside a price of $0.46 would read as
 * two figures of different precision rather than as one rate and its markup.
 */
function formatModelRate(raw: string, currency: string): string {
  const trimmed = raw.replace(/(\.\d{4}?)0+$/, "$1").replace(/\.$/, "");
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 6,
    }).format(Number(trimmed));
  } catch {
    return `${trimmed} ${currency}`;
  }
}

/**
 * The order categories are offered in, and the words they are offered under.
 *
 * Fixed rather than sorted by count, so the row does not reshuffle itself as the
 * catalogue grows and a reader can learn where "Embeddings" sits. Text leads
 * because it is what almost everybody arrives for.
 *
 * "Embeddings" is plural where the rest are singular: it names a job rather than
 * a medium, and "Embedding" reads as a verb.
 */
/** The shelves, in the order the rail offers them. Words in `ai.json`. */
export const MODEL_CATEGORIES: { key: ModelCategory }[] = [
  { key: "text" },
  { key: "image" },
  { key: "video" },
  { key: "embedding" },
  { key: "other" },
];

export function categoryLabel(category: ModelCategory, t: CatalogueT): string {
  return t(`category.${category}` as "category.text");
}

/**
 * A model's category, always.
 *
 * The API sends null for a row its catalogue sync has never scanned, which is
 * mostly a model an operator typed in by hand. Such a row still needs a shelf:
 * without one it would match no category, sit outside every filter but "all",
 * and look like a listing bug rather than a gap in a scan.
 *
 * Text is the fallback because it is what a hand-entered model almost always is,
 * and because Other is a shelf nobody browses. This used to consult `modality`
 * first, which could tell an embedding from everything else and nothing finer —
 * one shelf out of eight, bought with a column that no longer exists.
 */
export function categoryOf(model: AiModel): ModelCategory {
  return model.category ?? "text";
}

/**
 * All eight categories, in the fixed order above, each counted against whatever
 * the rest of the filters leave.
 *
 * All eight, including the ones this deployment has nothing in. Only per-token
 * models can be listed at all, so several are empty on most catalogues and stay
 * on screen anyway — the operator console has always done this, and the customer
 * catalogue now matches it. "Video 0" is a fact about what we carry; a missing
 * tab is silence, and silence reads as an oversight in a row that is otherwise
 * complete. It also holds the row still: eight tabs are eight tabs whatever the
 * filters do, so nothing moves under the cursor and nothing takes the selected
 * tab with it.
 *
 * Counted against every filter *except* the category itself. Count against the
 * full filter and every tab but the open one reads zero, which is the one place a
 * count tells you nothing. Excluded, the number answers the question actually
 * being asked at the moment of the click: given everything else I have chosen, is
 * there anything on that shelf.
 *
 * The order is fixed rather than by size. A row that reshuffles as the reader
 * types is a row nobody can learn.
 */
export function categoryCountsOf(
  matching: AiModel[],
): { key: ModelCategory; count: number }[] {
  const counts = new Map<ModelCategory, number>();
  for (const model of matching) {
    const key = categoryOf(model);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  return MODEL_CATEGORIES.map(({ key }) => ({ key, count: counts.get(key) ?? 0 }));
}

/**
 * The categories worth a chip, which is not every category.
 *
 * Every real one always, and `other` only when something is on it.
 *
 * The asymmetry matches the marketplace's. Text, Image, Video and Embeddings
 * are the product's taxonomy: a chip each holds the rail still, dimmed to zero
 * when a filter empties one, so nothing moves under the cursor and takes the
 * chip somebody was reaching for. `other` is not one of those — it appears only
 * when the API sends it, meaning a scan that could not name what the model
 * emits, and on a catalogue where that has not happened it is a label with no
 * possibility of stock.
 *
 * Counted across the whole catalogue rather than the filtered set, which is
 * what separates "empty here" from "empty everywhere". It is *not* the shelf
 * for a null category — `categoryOf` files those under Text.
 */
export function stockedCategoriesOf(
  models: AiModel[],
): { key: ModelCategory; count: number }[] {
  return categoryCountsOf(models).filter(({ key, count }) => key !== "other" || count > 0);
}

export interface ModelFilter {
  query: string;
  /**
   * One at a time, unlike publishers, and null for all of them.
   *
   * The odd one out here on purpose. Every other control on this page narrows a
   * set the reader is already looking at; this one changes which set that is —
   * text models and image models are different shelves, not two ticks of the same
   * box. Nobody browses "text or image" and compares the two; they pick a shelf.
   *
   * It reads as tabs for the same reason, and matches the operator console, which
   * settled on the same answer over the same catalogue.
   */
  category: ModelCategory | null;
  publishers: string[];
  capabilities: string[];
  /** Only models that cost nothing to call. */
  freeOnly: boolean;
  /**
   * Which pricing basis, and the question the two bases created.
   *
   * A rate card is a promise: you can work out a bill before you send anything.
   * A cost-priced model cannot make that promise — its charge is known after the
   * call. Somebody budgeting a workload cares about that difference more than
   * about any single rate on the page, and until now the only way to ask was to
   * read every row.
   *
   * Exclusive, like the category, because these are two answers to one question
   * rather than two conditions. "Fixed rate or per request" is every model.
   */
  pricing: "fixed" | "request" | null;
}

export const EMPTY_MODEL_FILTER: ModelFilter = {
  query: "",
  category: null,
  publishers: [],
  capabilities: [],
  freeOnly: false,
  pricing: null,
};

export function modelFilterIsEmpty(filter: ModelFilter): boolean {
  return (
    filter.query.trim() === "" &&
    filter.category === null &&
    filter.publishers.length === 0 &&
    filter.capabilities.length === 0 &&
    !filter.freeOnly &&
    filter.pricing === null
  );
}

/**
 * The catalogue, narrowed.
 *
 * Publishers are OR'd and capabilities are AND'ed, which is not an inconsistency
 * — they are different questions. Two publishers ticked means "either of these
 * vendors"; two capabilities ticked means "must do both", because a model that
 * streams but has no tool use is no use to somebody who needs tool use.
 *
 * The search covers the id as well as the name and description, since the id is
 * what somebody pastes in from a config file they are debugging.
 */
export function filterModels(models: AiModel[], filter: ModelFilter): AiModel[] {
  const query = filter.query.trim().toLowerCase();

  return models.filter((model) => {
    if (filter.category !== null && categoryOf(model) !== filter.category) return false;
    if (filter.publishers.length > 0 && !filter.publishers.includes(model.owned_by)) return false;
    if (!filter.capabilities.every((capability) => model.capabilities.includes(capability))) {
      return false;
    }
    if (filter.freeOnly && !isFreeModel(model)) return false;
    /* A free model is a rate card at zero, so it belongs under "fixed rate" —
       the question is whether the price is knowable beforehand, and nought is. */
    if (filter.pricing === "fixed" && isCostPriced(model)) return false;
    if (filter.pricing === "request" && !isCostPriced(model)) return false;

    if (query === "") return true;
    return [model.name, model.id, model.owned_by, model.description]
      .join(" ")
      .toLowerCase()
      .includes(query);
  });
}

/**
 * How the catalogue can be ordered.
 *
 * "Newest" is the default and leads, matching the public marketplace: `created`
 * is when Maxlayer first saw the model, so a new arrival surfaces without an
 * operator re-sorting anything. Cheapest is second because it is the question
 * somebody actually arrives with.
 *
 * Every comparator falls back to the id, so the order is total. Without that,
 * two models on the same rate swap places between renders and the list appears
 * to shuffle on its own.
 */
export const MODELS_PER_PAGE = 25;

export interface ModelPage {
  /** The requested page, brought into `1..pages`. Render from this, not the input. */
  page: number;
  pages: number;
  /** Slice bounds for the rows this page shows. */
  from: number;
  to: number;
  perPage: number;
  total: number;
}

/**
 * Which slice of the catalogue one page shows, with the page number clamped.
 *
 * Clamping is the point of having a function for this. Two things move the list
 * out from under a page number, and both end in a table that is empty for a
 * reason nothing on screen explains: a filter that narrows the list while the
 * reader is on page four, and a refetch that comes back shorter than the render
 * before it. The first is handled structurally in `ModelsTab` — the filter and
 * sort setters reset the page — and this is what catches the second, where there
 * is no event to hang a reset on.
 *
 * `total: 0` is still page one of one. The empty state renders there, and `pages:
 * 0` would make every `page === pages` comparison against it read wrong.
 *
 * The same function, by the same name, exists in `maxlayer-web`'s
 * `model-marketplace.mjs` for the public marketplace. Two copies rather than a
 * shared package: the repositories deploy separately and share no build, which is
 * the standing trade in this codebase.
 */
export function paginate(total: number, page: number, perPage = MODELS_PER_PAGE): ModelPage {
  const pages = Math.max(1, Math.ceil(total / perPage));
  const current = Math.min(Math.max(1, Math.trunc(page) || 1), pages);
  const from = (current - 1) * perPage;

  return { page: current, pages, from, to: Math.min(from + perPage, total), perPage, total };
}

/**
 * Both rate orders, differing only in direction — and in nothing else, which is
 * the point of writing them once.
 *
 * A cost-priced model has no rate to rank. Its micros are zero because the
 * provider publishes no figure, so a plain numeric compare made all 65 of them
 * the cheapest models in the catalogue: "Cheapest in" opened on a wall of image
 * and video models above every genuinely cheap text model, and the column of
 * prices beside them was empty. They sort to the end of *both* orders instead —
 * last is where a reader looks for "no answer", and it is the same answer to
 * cheapest and to dearest because the question does not apply either way.
 */
function byInputRate(a: AiModel, b: AiModel, direction: 1 | -1): number {
  const aRated = !isCostPriced(a);
  const bRated = !isCostPriced(b);
  if (aRated !== bRated) return aRated ? -1 : 1;

  return (
    direction * (rateMicros(a, "input") - rateMicros(b, "input")) || a.id.localeCompare(b.id)
  );
}

/** The orders offered, and the comparison each one means. Names in `ai.json`. */
export const MODEL_SORTS = [
  {
    key: "newest" as const,
    compare: (a: AiModel, b: AiModel) => b.created - a.created || a.id.localeCompare(b.id),
  },
  {
    key: "cheapest" as const,
    compare: (a: AiModel, b: AiModel) => byInputRate(a, b, 1),
  },
  {
    key: "dearest" as const,
    compare: (a: AiModel, b: AiModel) => byInputRate(a, b, -1),
  },
  {
    key: "context" as const,
    compare: (a: AiModel, b: AiModel) =>
      (b.context_window ?? 0) - (a.context_window ?? 0) || a.id.localeCompare(b.id),
  },
  {
    key: "name" as const,
    compare: (a: AiModel, b: AiModel) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id),
  },
];

export type ModelSortKey = (typeof MODEL_SORTS)[number]["key"];

export function modelSort(key: ModelSortKey): (typeof MODEL_SORTS)[number] {
  return MODEL_SORTS.find((sort) => sort.key === key) ?? MODEL_SORTS[0]!;
}

export function sortModels(models: AiModel[], key: ModelSortKey): AiModel[] {
  return [...models].sort(modelSort(key).compare);
}

/** Every publisher present, for the filter chips. Sorted, so they do not move. */
/**
 * The publishers in a catalogue, the ones with the most models first.
 *
 * Alphabetical put `ai21` above `openai` on a row the reader scans left to
 * right, which is the wrong end of a list that can run to forty names once a
 * sync has been through the whole supplier catalogue. Ordering by how many
 * models each has puts the shelves somebody is likely to want at the front,
 * which is what makes capping the row at a handful survivable.
 *
 * Ties fall back to the name so the order is total and does not shuffle between
 * renders.
 */
export function publishersOf(models: AiModel[]): string[] {
  const counts = new Map<string, number>();
  for (const model of models) {
    counts.set(model.owned_by, (counts.get(model.owned_by) ?? 0) + 1);
  }

  return [...counts.entries()]
    .sort(([aName, aCount], [bName, bCount]) => bCount - aCount || aName.localeCompare(bName))
    .map(([name]) => name);
}

export function capabilitiesOf(models: AiModel[]): string[] {
  return [...new Set(models.flatMap((model) => model.capabilities))].sort();
}

/**
 * What to call an API key in a spend breakdown.
 *
 * A deleted key still has spend against it and no name left to show, so it is
 * named as deleted rather than blank — a row labelled with nothing reads as a bug
 * rather than as history, and dropping it would leave a breakdown that does not
 * add up to the total beside it.
 */
export function keySpendLabel(
  row: { name: string | null; revoked: boolean },
  t: CatalogueT,
): string {
  if (row.name === null) return t("price.deletedKey");
  return row.revoked ? t("price.revokedKey", { name: row.name }) : row.name;
}

/** How long a model wears the "new" mark. */
export const NEW_MODEL_WINDOW_DAYS = 7;

/**
 * Whether the supplier published this model within that window.
 *
 * Reads `created` — OpenRouter's own publication date — rather than `listed_at`,
 * when this platform first carried it. It read `listed_at` until that field was
 * shown to be unable to carry the mark: the catalogue was seeded over five days
 * in August 2026, 328 of the 392 listed models in one run, so every listing date
 * fell inside this seven-day window together and each of them wore the badge at
 * the same time. A badge on every row is not a badge.
 *
 * The dates are not identical — they differ by milliseconds — which is why this
 * surfaced as a badge on everything rather than as a visible tie.
 *
 * What that trades away, said out loud: a model published long ago and listed
 * here yesterday is new to this customer and gets no mark. NEW answers "what has
 * the industry just released", not "what has this platform just stocked".
 *
 * `created` is unix *seconds*, not the ISO string `listed_at` was — the field is
 * OpenAI-compatible and typed that way. The API resolves it to the supplier's
 * date and falls back to our discovery date where the supplier published none.
 *
 * `now` is a parameter so a test can pin it. Absent, unreadable, at the epoch or
 * in the future is not new — the last would otherwise stick permanently.
 *
 * Duplicated from the marketplace's copy rather than shared, which is this
 * workspace's standing position on cross-repository code — see the error
 * envelope in CONVENTIONS.
 */
export function isNewModel(
  model: { created?: number | null } | null | undefined,
  now = Date.now(),
): boolean {
  const published = Number(model?.created);
  // `Number(null)` is 0, not NaN, so the epoch is excluded explicitly rather
  // than left to read as a very old model.
  if (!Number.isFinite(published) || published <= 0) return false;

  const age = now - published * 1000;
  return age >= 0 && age < NEW_MODEL_WINDOW_DAYS * 24 * 60 * 60 * 1000;
}
